import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronLeft, MailQuestion, CheckCircle2 } from "lucide-react";

const WEAVE_LOGO = "https://customer-assets-lxgj4vgw.emergentagent.net/job_essex-teams/artifacts/mur72jzi_WEAVE-LOGO%202024.webp";
const ESSEX_LOGO = "https://customer-assets.emergentagent.com/job_48a96b5b-7714-4454-98cb-57b574138d27/artifacts/xfc3bfwg_essex_business_school_logo.jpg";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null); // { found, reset_url, token, message }
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true); setResult(null);
    try {
      const res = await api.post("/auth/forgot-password", { email });
      setResult(res.data);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#FDFDFD]">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-between gap-4 mb-8">
          <img src={WEAVE_LOGO} alt="The Weave" className="h-16 w-auto object-contain" />
          <div className="font-display text-base sm:text-lg font-extrabold text-center leading-tight">
            The Weave<br />Workbook
          </div>
          <img src={ESSEX_LOGO} alt="Essex Business School" className="h-12 w-auto object-contain bg-white" />
        </div>

        <Link to="/login" className="inline-flex items-center text-xs uppercase tracking-wider text-[#525252] hover:text-[#0A0A0A] mb-6" data-testid="forgot-back">
          <ChevronLeft size={14} /> Back to sign in
        </Link>

        <div className="label-eyebrow">Forgot password</div>
        <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-2">Locked out?</h1>
        <p className="mt-3 text-sm text-[#525252]">
          Enter the email you registered with. We'll issue a single-use reset link valid for 24 hours.
        </p>

        {result ? (
          <div className="mt-8 space-y-4" data-testid="forgot-submitted">
            <div className="border-2 border-[#2596be] bg-[#E6F4FA] p-5 space-y-2">
              <div className="flex items-center gap-2 text-[#1e7da3]">
                <CheckCircle2 size={16} />
                <div className="label-eyebrow !text-[#1e7da3]">Check your inbox</div>
              </div>
              <p className="text-sm">
                If <strong>{email}</strong> is registered, we've emailed a single-use reset link. It's valid for 24 hours — check your junk folder if it hasn't arrived in a few minutes.
              </p>
              <p className="text-[11px] text-[#525252]">
                Still stuck? The programme team can set you a temporary password from the Student Roster.
              </p>
            </div>
            <Button
              type="button"
              onClick={() => { setResult(null); setEmail(""); }}
              className="h-9 rounded-none bg-white text-[#0A0A0A] border border-[#0A0A0A] hover:bg-[#F3F4F6] uppercase tracking-wide text-[11px] font-semibold"
              data-testid="forgot-reset-form-btn"
            >
              Request another
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="forgot-form">
            <div>
              <Label className="label-eyebrow">Email</Label>
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-none h-11"
                data-testid="forgot-email-input"
              />
            </div>
            {error && (
              <div className="border border-[#e93e36] bg-[#FDEDED] px-3 py-2 text-sm text-[#7A1F1F]" data-testid="forgot-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={busy} className="w-full h-11 rounded-none bg-[#2596be] hover:bg-[#1e7da3] uppercase tracking-wide text-xs font-semibold" data-testid="forgot-submit-btn">
              <MailQuestion size={14} className="mr-2" />
              {busy ? "Sending…" : "Request reset link"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
